import { gpx, kml } from "@tmcw/togeojson";

/** Что извлекаем из файла: границу территории или линию маршрута. */
export type GeoKind = "polygon" | "line";

type Position = [number, number];
type Geometry =
  | { type: "Polygon"; coordinates: Position[][] }
  | { type: "MultiPolygon"; coordinates: Position[][][] }
  | { type: "LineString"; coordinates: Position[] }
  | { type: "MultiLineString"; coordinates: Position[][] }
  | { type: string; coordinates?: unknown; geometries?: Geometry[] };

export interface ImportedGeometry {
  geometry: Geometry;
  /** Коротко для человека: что нашлось в файле. */
  summary: string;
}

/**
 * Разбирает GeoJSON, KML или GPX и возвращает одну геометрию нужного вида.
 * Все три формата по стандарту в WGS 84; файлы в другой системе координат
 * отвергнет сервер с понятным сообщением.
 */
export async function parseGeoFile(file: File, kind: GeoKind): Promise<ImportedGeometry> {
  const text = await file.text();
  const name = file.name.toLowerCase();

  let geometries: Geometry[];
  if (name.endsWith(".kml") || name.endsWith(".gpx")) {
    const doc = new DOMParser().parseFromString(text, "text/xml");
    if (doc.querySelector("parsererror")) throw new Error("Файл повреждён: не удалось прочитать XML");
    const fc = name.endsWith(".kml") ? kml(doc) : gpx(doc);
    geometries = fc.features.map((f) => f.geometry as Geometry | null).filter((g): g is Geometry => g !== null);
  } else {
    let json: unknown;
    try {
      json = JSON.parse(text);
    } catch {
      throw new Error("Файл не похож на GeoJSON: ожидается .geojson, .json, .kml или .gpx");
    }
    geometries = collect(json);
  }

  const flat = geometries.flatMap(flatten);
  const result = kind === "polygon" ? toPolygon(flat) : toLine(flat);
  // Файл в метрической системе (Пулково, UTM) иначе уронит карту превью:
  // такие «градусы» она не принимает.
  if (!inDegrees(result.geometry.coordinates)) {
    throw new Error("Координаты должны быть в WGS 84 — широта и долгота в градусах. Пересохраните файл в этой системе.");
  }
  return result;
}

function inDegrees(coords: unknown): boolean {
  if (!Array.isArray(coords)) return false;
  if (typeof coords[0] === "number") {
    const [lon, lat] = coords as number[];
    return lon !== undefined && lat !== undefined && Math.abs(lon) <= 180 && Math.abs(lat) <= 90;
  }
  return coords.every(inDegrees);
}

function collect(json: unknown): Geometry[] {
  const obj = json as { type?: string; features?: { geometry: Geometry | null }[]; geometry?: Geometry };
  if (obj?.type === "FeatureCollection") return (obj.features ?? []).map((f) => f.geometry).filter((g): g is Geometry => !!g);
  if (obj?.type === "Feature") return obj.geometry ? [obj.geometry] : [];
  if (obj?.type) return [obj as Geometry];
  throw new Error("В файле нет GeoJSON-геометрии");
}

function flatten(g: Geometry): Geometry[] {
  return g.type === "GeometryCollection" ? (g.geometries ?? []).flatMap(flatten) : [g];
}

function toPolygon(geoms: Geometry[]): ImportedGeometry {
  const polygons: Position[][][] = [];
  for (const g of geoms) {
    if (g.type === "Polygon") polygons.push(g.coordinates as Position[][]);
    if (g.type === "MultiPolygon") polygons.push(...(g.coordinates as Position[][][]));
  }
  if (polygons.length === 0) throw new Error("В файле нет полигона границы");
  const points = polygons.reduce((n, p) => n + (p[0]?.length ?? 0), 0);
  return {
    geometry: { type: "MultiPolygon", coordinates: polygons },
    summary: `${polygons.length === 1 ? "1 контур" : `${polygons.length} контуров`}, ${points} вершин`,
  };
}

function toLine(geoms: Geometry[]): ImportedGeometry {
  const lines: Position[][] = [];
  for (const g of geoms) {
    if (g.type === "LineString") lines.push(g.coordinates as Position[]);
    if (g.type === "MultiLineString") lines.push(...(g.coordinates as Position[][]));
  }
  if (lines.length === 0) throw new Error("В файле нет линии маршрута (трека)");
  const km = lines.reduce((sum, l) => sum + lengthM(l), 0) / 1000;
  const geometry: Geometry = lines.length === 1
    ? { type: "LineString", coordinates: lines[0] }
    : { type: "MultiLineString", coordinates: lines };
  return {
    geometry,
    summary: `${km.toFixed(1)} км, ${lines.reduce((n, l) => n + l.length, 0)} точек` +
      (lines.length > 1 ? ` в ${lines.length} частях — сервер их склеит, если они стыкуются` : ""),
  };
}

/** Длина по большому кругу — для подсказки до сохранения. */
function lengthM(line: Position[]): number {
  const rad = Math.PI / 180;
  let sum = 0;
  for (let i = 1; i < line.length; i++) {
    const a = line[i - 1];
    const b = line[i];
    if (!a || !b) continue;
    const h = Math.sin(((b[1] - a[1]) * rad) / 2) ** 2 +
      Math.cos(a[1] * rad) * Math.cos(b[1] * rad) * Math.sin(((b[0] - a[0]) * rad) / 2) ** 2;
    sum += 2 * 6371000 * Math.asin(Math.sqrt(h));
  }
  return sum;
}
